// Angular Imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Form, NgForm } from '@angular/forms';

// Third-party / Animations
import { biDirectionalState } from '../../animations/allAnimations';

// App-specific Imports
import { BaseComponent } from 'src/app/core/domain/base-component';
import { Entity } from 'src/app/core/data-models/Entity';
import { ModuleName } from '../../enums/ModuleName';
import { CriteriaType } from '../../enums/CriteriaType.enum';
import { PredefinedMetricsService } from '@ex/module/core/data-models/predefined-metrics.service';
import { AnalysisFilterKeys } from '@ex/module/core/domain/analysis-filter-keys';
import { CommonChartFunctions } from '@ex/module/core/services/chart-data.service';

@Component({
  selector: 'q-metrics',
  templateUrl: './q-metrics.component.html',
  styleUrls: ['./q-metrics.component.scss'],
  animations: [biDirectionalState]
})
export class QMetricsComponent extends BaseComponent implements OnInit {

  // Inputs
  @Input() title: string;
  @Input() colorTheme: string;
  @Input() predefinedMetricsDone: boolean;
  @Input() resetDropdown: boolean;
  @Input() language: string;
  @Input() companyKey: string;
  @Input() entities: Entity[];
  @Input() disableSnapshotMetrics: boolean;

  @Input('metricsList') set metrs(value: any) {
    this.selectedCompany = this.entities?.find(s => s.key == this.companyKey);
    const metrics = this.disableSnapshotMetrics ? this.predefinedMetrics.extractAndOverrideMetrics(value, this.selectedCompany, this.entities, true) : value;
    setTimeout(() => {
      const orderedEntities = this.selectedCompany.EntityGroups.map(x => x.groupKey);
      const defaultMetricOrder = Object.keys(AnalysisFilterKeys).reduce((acc: any[], x) => {
        const metric = metrics.find((m: any) => x.toLowerCase().includes(m.key.toLowerCase()) || m.key.toLowerCase() == x.toLowerCase());
        if (metric?.key && !orderedEntities.includes(metric.key)) {
          acc.push(metric.key);
        }
        return acc;
      }, []);
      defaultMetricOrder.push(...orderedEntities);
      this.metrics = this.commonChartFunctions.reArrangeArray(metrics, defaultMetricOrder, 'key');
    }, 500);
  }


  // Outputs
  @Output() proceed = new EventEmitter<void>();

  // Public properties
  ModuleNames = ModuleName;
  metrics: any;
  selectedCompany: Entity;
  sharedParentEntities: any;
  submitted = false;
  proceeded = false;
  step = 0;
  childStep = 0;
  today = new Date();
  searchValue: string;
  tempList: any[];
  maxDateOfBirth: Date = new Date(this.today.getFullYear() - 18, this.today.getMonth(), this.today.getDate());
  multiUseTimer: any;

  constructor(private predefinedMetrics: PredefinedMetricsService, private commonChartFunctions: CommonChartFunctions) {
    super();
  }

  ngOnInit(): void {
    if (!this.selectedCompany && this.entities) {
      this.selectedCompany = this.entities.find(s => s.key == this.companyKey);
    }
  }

  searchList(e: string, array: any[]): void {
    this.tempList = array.filter(s => s.name.find(n => n.name.toLowerCase().includes(e.toLowerCase())));
  }

  resolveTempList(metric: any): boolean {
    if (!this.tempList?.length) {
      this.tempList = metric.options;
    }
    return metric.options.length > 6;
  }

  dropDownChanged(event: any, metric: any): void {
    metric.value = event;
    if (event === '') {
      this.resetDropdown = true;
      this.multiUseTimer = setTimeout(() => {
        this.resetDropdown = false;
      }, 50);
    }
  }

  formateExistingDate(date: Date, metric: any, f?: boolean): string {
    if (!date) return "";
    const newDate = new Date(date);
    const formattedDate = f
      ? `${newDate.getMonth() + 1}/${newDate.getDate()}/${newDate.getFullYear()}`
      : `${newDate.getDate()}/${newDate.getMonth() + 1}/${newDate.getFullYear()}`;
    metric.value = formattedDate;
    return formattedDate;
  }

  addAsteriskToLabel(label: string): string {
    return `${label} *`;
  }

  setReminderDate(e: Date | any, metric: any): void {
    if (e instanceof Date) {
      metric.value = new Date(e).toISOString();
    } else {
      metric.value = null;
    }
  }

  updateComplexMetric(option: any, metric: any, index: number): void {
    if (!this.selectedCompany) {
      this.selectedCompany = this.entities?.find(s => s.key == this.companyKey);
    }

    metric.value = option?.key;
    if (metric.isEntity) {
      this.entityGroupChanged(option?.key, metric.key);
    }

    let escape = false;
    for (let i = 0; i < this.metrics.length; i++) {
      if (this.metrics[i + 1]?.options?.length && !escape) {
        this.step++;
      } else {
        escape = true;
      }
    }

    if (this.step === this.metrics.length) {
      this.proceed.emit();
    }
    this.tempList = [];
  }

  checkIfSkippable(metric: any): boolean {
    const currentEntitySelectionKey = this.selectedCompany?.EntityGroups.find(s => s?.groupKey === metric.key)?.parentKey;
    this.sharedParentEntities = this.selectedCompany?.EntityGroups.filter(s => s.parentKey === currentEntitySelectionKey && this.metrics.find(m => m.key === s.groupKey));

    if (this.sharedParentEntities?.length < 2) return false;

    const entityFound = this.sharedParentEntities.find(e => e.groupKey === metric.key);

    return !!(entityFound &&
      (this.sharedParentEntities.indexOf(entityFound) !== this.sharedParentEntities.length ||
        this.sharedParentEntities.find(s => s.value !== "")));
  }

  eraseValuesForEntityGroups(groupKey: string): void {
    const selectedEntity = this.entities.find(e => e.key === this.companyKey);
    const reportingEntityGroups = selectedEntity?.EntityGroups.filter(eg => eg.parentKey === groupKey) || [];

    reportingEntityGroups.forEach(eG => {
      const eM = this.metrics.find(m => m.key === eG.groupKey);
      if (eM) {
        eM.value = "";
        this.entityGroupChanged('', eG.groupKey);
      }
    });
  }

  entityGroupChanged(event: any, groupKey: string): void {
    const selectedEntity = this.entities?.find(e => e.key === this.companyKey);
    const reportingEntityGroups = selectedEntity?.EntityGroups.filter(eg => eg.parentKey === groupKey) || [];

    this.eraseValuesForEntityGroups(groupKey);

    reportingEntityGroups.forEach(eG => {
      const eM = this.metrics.find(m => m.key === eG.groupKey);
      if (eM) {
        eM.options = [];
        const entitiesInGroup = this.entities.filter(e => e.entityGroupKey === eG.groupKey && e.parentKey === event);

        eM.options = entitiesInGroup.map(entity => ({
          key: entity.key,
          name: entity.name,
          parentEntityName: this.entities.find(e => e.key === entity.parentKey)?.name || []
        }));

        eM.required = entitiesInGroup.length > 0;
      }
    });
  }

  updateCharacterCount(metric: any): void {
    console.log("metric", metric);
  }

  submit(form: NgForm): void {
    this.submitted = true;
    if (!form.valid) {
      return this.SnackbarService.error(this.localize('txtFillAllCriteriaFields', this.language, this.ModuleNames.Surveys));
    }
    this.metrics = this.metrics.map(metric => ({
      ...metric,
      type: metric.type || CriteriaType.DropDown
    }));
    this.proceed.emit();
  }

  doNothing(): void { }
}
